import React, { useMemo, useState } from 'react';
import { useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';

import {
  LeafletMap,
  LeafletMarkerLayer,
} from '@uncinc/react-leaflet';

import {
  TileLayer, ZoomControl,
} from 'react-leaflet';

import { LeafletMarker } from '@uncinc/react-leaflet/dist/types';

import {
  LEAFLET_MAP_CENTER,
  LEAFLET_MAP_OFF_CENTER,
  SCREEN_HUGE,
  LEAFLET_MAP_ZOOM,
  LEAFLET_MAP_BOUNDS,
  LEAFLET_MAP_MAX_BOUNDS_VISCOSITY,
  LEAFLET_MAP_FOCUS_PADDING_DESKTOP,
  LEAFLET_MAP_FOCUS_PADDING_TABLET,
  SCREEN_NORMAL,
  SCREEN_WIDE,
  LEAFLET_MAP_FOCUS_PADDING_MOBILE,
} from '../../../../config/constants';

import { LeafletMapProps, MarkerType } from '../../types';
import { LeafletPopup, popupContentConstructor } from '../LeafletPopup';
import transformHitsToMarkers from '../../helpers/transformHitsToMarkers';

const LeafletMapRender: React.FC<LeafletMapProps> = React.memo((props) => {
  const {
    hits,
    setSelectedMarker,
    defaultMarker,
    mapRef,
  } = props;

  const customPopupContentConstructor = (marker: LeafletMarker) => {
    setSelectedMarker(marker);
    return popupContentConstructor();
  };

  const { width } = useWindowDimensions();
  const markers = transformHitsToMarkers(hits, defaultMarker);

  const focusPadding = useMemo(() => {
    if (width > SCREEN_WIDE) {
      return LEAFLET_MAP_FOCUS_PADDING_DESKTOP;
    }

    if (width > SCREEN_NORMAL) {
      return LEAFLET_MAP_FOCUS_PADDING_TABLET;
    }

    return LEAFLET_MAP_FOCUS_PADDING_MOBILE;
  }, [width]);

  return (
    <LeafletMap
      ref={mapRef}
      center={width > SCREEN_HUGE ? LEAFLET_MAP_CENTER : LEAFLET_MAP_OFF_CENTER}
      zoom={LEAFLET_MAP_ZOOM.DEFAULT}
      scrollWheelZoom={false}
      maxZoom={LEAFLET_MAP_ZOOM.MAX}
      minZoom={LEAFLET_MAP_ZOOM.MIN}
      maxBounds={LEAFLET_MAP_BOUNDS}
      maxBoundsViscosity={LEAFLET_MAP_MAX_BOUNDS_VISCOSITY}
      touchZoom
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
      />
      <ZoomControl position="topright" />
      <LeafletMarkerLayer
        markers={markers}
        popupContentConstructor={customPopupContentConstructor}
        showClusters={false}
        focusPadding={focusPadding}
      />
    </LeafletMap>
  );
});

const MapWithSearchResults: React.FC<Omit<LeafletMapProps, 'setSelectedMarker'>> = (props) => {
  const {
    hits,
    defaultMarker,
    mapRef,
  } = props;

  const [selectedMarker, setSelectedMarker] = useState<MarkerType>();

  return (
    <>
      {selectedMarker && (
        <LeafletPopup
          title={selectedMarker.title}
          url={selectedMarker.url}
          projectStatus={selectedMarker.project_status}
          projectType={selectedMarker.project_type}
        />
      )}
      <LeafletMapRender
        mapRef={mapRef}
        hits={hits}
        setSelectedMarker={setSelectedMarker}
        defaultMarker={defaultMarker}
      />
    </>
  );
};

export default MapWithSearchResults;
